import React, {useContext, useEffect, useState} from 'react';
import './NRS_USDC_LP_Staking.scss';
import StakeButtons from "./StakeButtons";
import {earnBalanceStore} from "../../../stores/EarnBalanceStore";
import {useAccount, useBlockNumber} from "wagmi";
import {ContractContext} from "../../../context-providers/ContractsProvider";
import {ServicesContext} from "../../../context-providers/ServicesProvider";
import {useBreakpointContext} from "../../../context-providers/BreakpointProvider";
import NereusButton from "../../NereusButton/NereusButton";
import HelpSection from "../HelpSection/HelpSection";
import {contractAddresses} from "../../../helpers/config/contracts.config";
import apiService from "../../../services/api/api.service";

const SECONDS_IN_YEAR = 365n * 24n * 60n * 60n;
const NRS_DECIMALS = 18;
const USDC_DECIMALS = 6;
const LP_DECIMALS = 18;

const NRS_USDC_LP_Staking = () => {
    const { xs, sm, md } = useBreakpointContext();
    const { data: blockNumber } = useBlockNumber({ watch: true });
    const { address: userAddress } = useAccount();
    const { nrsContract, lpNrsUsdcContract, lpNrsUsdcStakingContract, lpNrsUsdcPoolContract } = useContext(ContractContext);
    const { depositWithdrawalEarnService } = useContext(ServicesContext);
    const [apr, setApr] = useState('')
    const [yearlyEarnEstimate, setYearlyEarnEstimate] = useState('')
    const [totalEarned, setTotalEarned] = useState('')
    const [nrsPrice, setNrsPrice] = useState<number | null>(null)

    // Load cached APR data on mount
    useEffect(() => {
        const cached = earnBalanceStore.getNrsUsdcAprCache();
        if (cached) {
            setApr(cached.apr);
            setYearlyEarnEstimate(cached.yearlyEarnEstimate);
            setTotalEarned(cached.totalEarned);
        }
    }, []);

    useEffect(() => {
        if (!userAddress) {
            earnBalanceStore.resetState()
        }
    }, [userAddress]);

    useEffect(() => {
        if (userAddress && blockNumber !== undefined) {
            earnBalanceStore.fetchBalancesNRS_USDCThrottled(nrsContract, lpNrsUsdcContract, lpNrsUsdcStakingContract, userAddress);
        }
    }, [userAddress, nrsContract, lpNrsUsdcContract, blockNumber]);

    // Fetch NRS price
    useEffect(() => {
        async function fetchNrsPrice() {
            try {
                const data = await apiService.priceGet('NRS');
                setNrsPrice(data.price);
            } catch (err) {
                console.error('Failed to fetch NRS price:', err);
            }
        }
        fetchNrsPrice();
    }, []);

    // Fetch claimable rewards (earned NRS)
    useEffect(() => {
        async function fetchClaimableRewards() {
            try {
                if (lpNrsUsdcStakingContract && userAddress) {
                    const claimableRewards = await lpNrsUsdcStakingContract.read.getClaimableRewards([userAddress]);
                    const earnedNrs = Number(claimableRewards) / 10 ** NRS_DECIMALS;
                    setTotalEarned(earnedNrs.toFixed(4));
                }
            } catch (err) {
                console.error('Failed to fetch claimable rewards:', err);
            }
        }
        fetchClaimableRewards();
    }, [userAddress, lpNrsUsdcStakingContract, blockNumber]);

    // Calculate APR and yearly estimate
    useEffect(() => {
        async function calculateApr() {
            try {
                if (!lpNrsUsdcStakingContract || !lpNrsUsdcPoolContract || nrsPrice === null) {
                    return;
                }

                // Check if cache is still valid - skip RPC calls if so
                const cached = earnBalanceStore.getNrsUsdcAprCache();
                if (cached) {
                    return;
                }

                const totalStakedAmount = await lpNrsUsdcStakingContract.read.getTotalStakedAmount();

                if (totalStakedAmount === 0n) {
                    setApr('N/A');
                    return;
                }

                const emissionPoint = await lpNrsUsdcStakingContract.read.getEmissionPoint();
                if (!emissionPoint) {
                    return;
                }

                const rewardTokensPerSecond = emissionPoint.rewardTokensPerSecond;

                // Get pool reserves and total supply to calculate LP token price
                const [reserve0, reserve1] = await lpNrsUsdcPoolContract.read.getReserves();
                const lpTotalSupply = await lpNrsUsdcPoolContract.read.totalSupply();
                const token0 = await lpNrsUsdcPoolContract.read.token0();

                // Determine which token is NRS and which is USDC
                const nrsAddress = contractAddresses.base.nrs.toLowerCase();
                const isToken0Nrs = token0.toLowerCase() === nrsAddress;

                let nrsReserve: bigint;
                let usdcReserve: bigint;

                if (isToken0Nrs) {
                    nrsReserve = reserve0;
                    usdcReserve = reserve1;
                } else {
                    nrsReserve = reserve1;
                    usdcReserve = reserve0;
                }

                // Calculate LP token price in USD
                // LP price = (nrsReserve * nrsPrice + usdcReserve * usdcPrice) / lpTotalSupply
                // USDC price = 1 USD
                const nrsValueInUsd = (Number(nrsReserve) / 10 ** NRS_DECIMALS) * nrsPrice;
                const usdcValueInUsd = Number(usdcReserve) / 10 ** USDC_DECIMALS;
                const totalPoolValueInUsd = nrsValueInUsd + usdcValueInUsd;
                const lpPriceInUsd = totalPoolValueInUsd / (Number(lpTotalSupply) / 10 ** LP_DECIMALS);

                // Calculate APR
                // APR = (yearly rewards in USD) / (total staked value in USD) * 100
                //     = (rewardTokensPerSecond * secondsInYear * NRS_PRICE) / (totalStaked * LP_PRICE) * 100
                const yearlyRewardsInNrs = Number(rewardTokensPerSecond) / 10 ** NRS_DECIMALS * Number(SECONDS_IN_YEAR);
                const yearlyRewardsInUsd = yearlyRewardsInNrs * nrsPrice;
                const totalStakedValueInUsd = (Number(totalStakedAmount) / 10 ** LP_DECIMALS) * lpPriceInUsd;

                const aprValue = (yearlyRewardsInUsd / totalStakedValueInUsd) * 100;
                const aprStr = `${aprValue.toFixed(2)}%`;
                setApr(aprStr);

                // Calculate yearly NRS earnings estimate for user
                const userStakedAmount = BigInt(earnBalanceStore.lpNrsUsdcStakingBalance || '0');
                let yearlyEstStr = '0';
                if (userStakedAmount > 0n && totalStakedAmount > 0n) {
                    const userShareOfPool = Number(userStakedAmount) / Number(totalStakedAmount);
                    const yearlyNrsEarnings = yearlyRewardsInNrs * userShareOfPool;
                    yearlyEstStr = yearlyNrsEarnings.toFixed(4);
                }
                setYearlyEarnEstimate(yearlyEstStr);

                // Update cache (totalEarned is updated separately)
                earnBalanceStore.setNrsUsdcAprCache(aprStr, yearlyEstStr, totalEarned);

            } catch (err) {
                console.error('Failed to calculate APR:', err);
            }
        }
        calculateApr();
    }, [lpNrsUsdcStakingContract, lpNrsUsdcPoolContract, nrsPrice, blockNumber, totalEarned]);

    const handleClaim = async () => {
        try {
            await depositWithdrawalEarnService.claimFromNRS_USDC_LP_Staking();
        } catch (err) {
            console.error('Failed to claim:', err);
        }
    };

    const claimButton = (
        <div>
            <NereusButton className="flex-1" variant="contained" onClick={handleClaim}>
                Withdraw
            </NereusButton>
        </div>
    )

    return (
        <div className="NXUSD_USDC_LP_Staking justify-start flex-col flex-grow">
            {md && (
                <div className="EarnPage__header flex md">
                    <div className="stats card-container flex">
                        <div className="stats__apy flex-col">
                            <label className="nereus-caption">APR</label>
                            <div className={`nereus-body-m`}>{apr}</div>
                        </div>
                        <div className="flex-col">
                            <label className="nereus-caption nereus-color-neutral-grey">Yearly earn NRS</label>
                            <div className={`nereus-body-m`}>{yearlyEarnEstimate}</div>
                        </div>
                        <div className="flex-col">
                            <label className="nereus-caption nereus-color-neutral-grey">Earned NRS</label>
                            <div className={`nereus-body-m`}>{totalEarned}</div>
                        </div>
                        <div className="buttons flex flex-grow align-item">
                            {claimButton}
                        </div>
                    </div>
                    <div className="balances card-container flex">
                        <div className="flex-col">
                            <label className="nereus-caption nereus-color-neutral-grey">Wallet&nbsp;Balance&nbsp;·&nbsp;NRS-USDC&nbsp;LP</label>
                            <div className={`nereus-body-m`}>{earnBalanceStore.lpNrsUsdcTokenBalanceDecimal}</div>
                        </div>
                        <div className="flex-col">
                            <label className="nereus-caption nereus-color-neutral-grey">Staked&nbsp;·&nbsp;NRS-USDC&nbsp;LP</label>
                            <div className={`nereus-body-m`}>{earnBalanceStore.lpNrsUsdcStakingBalanceDecimal}</div>
                        </div>
                        <div className="buttons flex flex-grow align-item">
                            <StakeButtons/>
                        </div>
                    </div>
                </div>
            )}
            {sm && (
                <div className="EarnPage__header flex flex-col">

                    <div className="balances card-container flex">
                        <div className="flex-col">
                            <label
                                className="nereus-caption nereus-color-neutral-grey">Wallet&nbsp;Balance&nbsp;NRS</label>
                            <div className={`nereus-body-m`}>{earnBalanceStore.lpNrsUsdcTokenBalanceDecimal}</div>
                        </div>
                        <div className="flex-col">
                            <label className="nereus-caption nereus-color-neutral-grey">Staked&nbsp;·&nbsp;NRS-USDC&nbsp;LP</label>
                            <div className={`nereus-body-m`}>{earnBalanceStore.lpNrsUsdcStakingBalanceDecimal}</div>
                        </div>
                        <div className="buttons flex flex-grow">
                            <StakeButtons/>
                        </div>
                    </div>
                    <div className="stats card-container flex">
                        <div className="stats__apy flex-col">
                            <label className="nereus-caption">APR</label>
                            <div className={`nereus-body-m`}>{apr}</div>
                        </div>
                        <div className="flex-col">
                            <label className="nereus-caption nereus-color-neutral-grey">Yearly earn NRS</label>
                            <div className={`nereus-body-m`}>{yearlyEarnEstimate}</div>
                        </div>
                        <div className="flex-col">
                            <label className="nereus-caption nereus-color-neutral-grey">Earned NRS</label>
                            <div className={`nereus-body-m`}>{totalEarned}</div>
                        </div>
                        <div className="buttons flex flex-grow">
                            {claimButton}
                        </div>
                    </div>
                </div>
            )}
            {xs && (
                <div className="EarnPage__header flex flex-col">
                <div className="balances card-container flex-col">
                        <div className="flex">
                            <div className="flex-col">
                                <label className="nereus-caption nereus-color-neutral-grey">Wallet&nbsp;Balance&nbsp;NRS</label>
                                <div className={`nereus-body-m`}>{earnBalanceStore.lpNrsUsdcTokenBalanceDecimal}</div>
                            </div>
                            <div className="flex-col">
                                <label className="nereus-caption nereus-color-neutral-grey">Staked&nbsp;·&nbsp;NRS-USDC&nbsp;LP</label>
                                <div className={`nereus-body-m`}>{earnBalanceStore.lpNrsUsdcStakingBalanceDecimal}</div>
                            </div>
                        </div>
                        <div className="buttons flex flex-grow">
                            <StakeButtons/>
                        </div>
                    </div>
                    <div className="stats card-container flex">
                        <div className="stats__apy flex-col">
                            <label className="nereus-caption">APR</label>
                            <div className={`nereus-body-m`}>{apr}</div>
                        </div>
                        <div className="flex-col">
                            <label className="nereus-caption nereus-color-neutral-grey">Yearly earn NRS</label>
                            <div className={`nereus-body-m`}>{yearlyEarnEstimate}</div>
                        </div>
                        <div className="flex-col">
                            <label className="nereus-caption nereus-color-neutral-grey">Earned NRS</label>
                            <div className={`nereus-body-m`}>{totalEarned}</div>
                        </div>
                        <div className="buttons flex flex-grow">
                            {claimButton}
                        </div>
                    </div>
                </div>
            )}
            <HelpSection/>
        </div>
    );
}

export default NRS_USDC_LP_Staking;